import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {useParams} from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import RouteContainer from '../../../../common/styles/RouteContainer'
import RouteTitle from '../../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../../config/BooksitoutIcon'
import RouteTitleConfig from '../../../../config/RouteTitleConfig'
import RowSpacer from '../../../../common/styles/RowSpacer'
import {BooksitoutServer} from '../../../../config/BooksitoutServer'
import BookResponse from '../../BookResponse'
import BookMineDoneCard from '../../mine/BookMineDoneCard'
import BookMineCard from '../../mine/BookMineCard'
import NoContentForFullScreen from '../../../../common/NoContentForFullScreen'
import useHtmlTitle from '../../../../common/useHtmlTitle'
import BookMineLoadingCard from '../../mine/BookMineLoadingCard'

const categoryNameMap: Record<string, string> = {
	OTHERS: '총류',
	PHILOSOPHY: '철학',
	RELIGION: '종교',
	SOCIAL_SCIENCE: '사회과학',
	NATURAL_SCIENCE: '자연과학',
	TECHNOLOGY: '기술',
	ART: '예술',
	LANGUAGE: '언어',
	LITERATURE: '문학',
	HISTORY: '역사',
	UNKNOWN: '?',
}

const BookStatisticsCategoryBooksRoute = () => {
	const {category} = useParams<{ category: string }>()
	const categoryName = categoryNameMap[category ?? ''] ?? category

	useHtmlTitle(`${categoryName} | 장르별 독서`)

	const [books, setBooks] = useState<BookResponse[]>([])
	const [totalCount, setTotalCount] = useState<number>(0)
	const [isLoading, setIsLoading] = useState(true)
	const [page, setPage] = useState(1)
	const [isLast, setIsLast] = useState(false)
	const size = 32

	useEffect(() => {
		if (!category) return

		BooksitoutServer
			.get(`/v1/statistics/categories/${category}/books?page=${page}&size=${size}`)
			.then((res) => {
				setBooks(prev => page === 1 ? res.data.content : [...prev, ...res.data.content])
				setTotalCount(res.data.totalElements ?? 0)
				setIsLast(res.data.last ?? true)
			})
			.catch(() => setBooks([]))
			.finally(() => setIsLoading(false))
	}, [category, page])

	const fetchNext = () => setPage(prev => prev + 1)

	return (
		<RouteContainer>
			<RouteTitle
				icon={<booksitoutIcon.statistics/>}
				title={'통계'}
				subTitle={'내 독서활동에 대한 통계를 확인할 수 있어요'}
				currentKey={'statistics'}
				buttons={RouteTitleConfig.Book}
				rightUi={undefined}
			/>

			<RowSpacer size={40}/>

			<ListHeader>
				<CategoryName>{categoryName}</CategoryName>
				{!isLoading && books.length > 0 && (
					<CountIndicator>{totalCount}권</CountIndicator>
				)}
			</ListHeader>

			<RowSpacer size={10}/>

			{isLoading ? (
				<Row>
					{Array.from({length: 12}).map((_, i) => (
						<Col key={i}>
							<BookMineLoadingCard/>
						</Col>
					))}
				</Row>
			) : books.length === 0 ? (
				<NoContentForFullScreen message={`${categoryName} 장르에 속한 책이 없어요`}/>
			) : (
				<InfiniteScroll
					loader={<Row>{Array.from({length: 12}).map((_, i) => <Col key={i}><BookMineLoadingCard/></Col>)}</Row>}
					next={fetchNext}
					hasMore={!isLast}
					dataLength={books.length}
					className="overflow-hidden"
				>
					<Row>
						{books.map(book => (
							<Col key={book.id}>
								{book.status === 'DONE' ? (
									<BookMineDoneCard book={book}/>
								) : (
									<BookMineCard book={book}/>
								)}
							</Col>
						))}
					</Row>
				</InfiniteScroll>
			)}
		</RouteContainer>
	)
}

const Row = styled.div.attrs({
	className: 'row',
})``

const ListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 8px;
`

const CategoryName = styled.h4`
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
`

const CountIndicator = styled.h5`
	font-weight: bold;
	color: var(--primary-color);
	margin: 0;
`

const Col = styled.div.attrs({
	className: 'col-6 col-md-4 col-lg-3 col-xl-2',
})`
	margin-top: 30px;
	margin-bottom: 30px;
	height: 300px;

	& > a {
		display: block;
		height: 100%;
	}

	.clickable {
		height: 100%;
		padding-top: 5px;
		padding-bottom: 5px;
	}
`

export default BookStatisticsCategoryBooksRoute
